/*
 * rheincool — Storefront-Filtering (HANDOFF.md §5.4)
 * <rc-facets-page data-section-id>:
 * - Filteränderung/Sortierung → Section Rendering API → #CollectionContent
 *   wird ersetzt, URL via history.replaceState aktualisiert
 * - Chips (data-facet-remove) entfernen Filter ohne Reload
 * - Mobil: Bottom-Sheet mit Fokusfalle + Escape
 * - Tracking: filter_apply / filter_clear
 */
class RcFacetsPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.debounceTimer = null;
    this.bindAll();
  }

  bindAll() {
    this.bindForm();
    this.bindChips();
    this.bindSort();
    this.bindSheet();
  }

  bindForm() {
    const form = this.querySelector('[data-facets-form]');
    if (!form) return;
    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-facet-price]')) return;
      this.applyFromForm(form);
      if (window.rcTrack) {
        window.rcTrack('filter_apply', { name: event.target.name, value: event.target.value });
      }
    });
    form.addEventListener('input', (event) => {
      if (!event.target.matches('[data-facet-price]')) return;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.applyFromForm(form), 600);
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applyFromForm(form);
    });
  }

  bindChips() {
    this.querySelectorAll('[data-facet-remove]').forEach((chip) => {
      chip.addEventListener('click', (event) => {
        event.preventDefault();
        if (window.rcTrack) window.rcTrack('filter_clear', { url: chip.getAttribute('href') });
        this.load(chip.getAttribute('href'));
      });
    });
  }

  bindSort() {
    const sort = this.querySelector('[data-facet-sort]');
    if (!sort) return;
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      this.load(url.pathname + url.search);
    });
  }

  /* ---------- Bottom-Sheet (mobil) ---------- */

  bindSheet() {
    this.facetsEl = this.querySelector('#rc-facets');
    this.querySelectorAll('[data-facets-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openSheet());
    });
    this.querySelectorAll('[data-facets-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeSheet());
    });
    if (this.facetsEl) {
      this.facetsEl.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeSheet();
        if (event.key === 'Tab' && this.facetsEl.classList.contains('is-open')) {
          this.trapFocus(event);
        }
      });
    }
  }

  openSheet() {
    this.lastFocused = document.activeElement;
    this.facetsEl.classList.add('is-open');
    document.body.classList.add('rc-no-scroll');
    const first = this.facetsEl.querySelector('button, input:not([disabled])');
    if (first) first.focus();
  }

  closeSheet() {
    this.facetsEl.classList.remove('is-open');
    document.body.classList.remove('rc-no-scroll');
    if (this.lastFocused) this.lastFocused.focus();
  }

  trapFocus(event) {
    const focusables = this.facetsEl.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), a[href], select'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- AJAX ---------- */

  applyFromForm(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    this.load(`${window.location.pathname}?${params.toString()}`);
  }

  async load(url) {
    const container = this.querySelector('#CollectionContent');
    if (!container) return;
    container.classList.add('is-loading');
    const wasSheetOpen = this.facetsEl && this.facetsEl.classList.contains('is-open');

    try {
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`);
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fresh = parsed.querySelector('#CollectionContent');
      if (fresh) {
        container.innerHTML = fresh.innerHTML;
        history.replaceState({}, '', url);
        this.bindAll();
        if (wasSheetOpen) {
          this.facetsEl = this.querySelector('#rc-facets');
          if (this.facetsEl) this.facetsEl.classList.add('is-open');
        }
      }
    } catch (error) {
      console.warn('[rc-facets]', error);
      window.location.href = url;
    } finally {
      const after = this.querySelector('#CollectionContent');
      if (after) after.classList.remove('is-loading');
    }
  }
}

customElements.define('rc-facets-page', RcFacetsPage);
